<template>
  <div v-if="!noActions" class="ActionRenderer__Buttons">
    <b-button-group>
      <b-button
        v-for="(button, key) in buttons"
        :key="key"
        :title="button.title"
        @click="button.action"
        :disabled="button.disabled"
      >
        <font-awesome-icon :icon="button.icons" />
      </b-button>
    </b-button-group>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator'
import { ICellRendererParams } from 'ag-grid-community'

interface Params extends ICellRendererParams {
  [key: string]: any
}

@Component
export default class ActionRenderer extends Vue {
  params!: Params
  noActions = false
  noConfirm = false

  buttons = [] as {
    title: string
    icons: string[]
    condition: Function
    action: Function
    disabled?: boolean
  }[]

  created() {
    if (this.params.data) {
      if (this.params.data._noActions) {
        this.noActions = true
      }
      if (this.params.data._noConfirm) {
        this.noConfirm = true
      }
    }
    this.rebuildButtons()
  }

  checkType(type: string) {
    return this.params.getActionByType
      ? this.params.getActionByType(this.params).includes(type)
      : true
  }

  private rebuildButtons() {
    const buttons = [
      {
        title: this.params.onPrintText ?? 'Печать',
        icons: ['fas', 'print'],
        condition: () => {
          return this.params.onPrint && this.checkType('onPrint')
        },
        action: () => {
          this.params.onPrint(this.params)
        }
      },
      {
        title: this.params.onConfirmText ?? 'Подтвердить',
        icons: ['fas', 'check'],
        condition: () => {
          return this.params.onConfirm && this.checkType('onConfirm')
        },
        action: () => {
          this.params.onConfirm(this.params)
        }
      },
      {
        title: this.params.onDownloadText ?? 'Скачать',
        icons: ['fas', 'file-download'],
        condition: () => {
          return this.params.onDownload && this.checkType('onDownload')
        },
        action: () => {
          this.params.onDownload(this.params)
        }
      },
      {
        title: this.params.onEditText ?? 'Редактировать',
        icons: ['fas', 'edit'],
        condition: () => {
          return this.params.onEdit && this.checkType('onEdit')
        },
        action: () => {
          this.params.onEdit(this.params)
        }
      },
      {
        title: this.params.onMoveUpText ?? 'Переместить выше',
        icons: ['fas', 'arrow-up'],
        condition: () => {
          return this.params.onMoveUp && this.checkType('onMoveUp')
        },
        action: () => {
          this.params.onMoveUp(this.params)
        }
      },
      {
        title: this.params.onMoveDownText ?? 'Переместить ниже',
        icons: ['fas', 'arrow-down'],
        condition: () => {
          return this.params.onMoveDown && this.checkType('onMoveDown')
        },
        action: () => {
          this.params.onMoveDown(this.params)
        }
      },
      {
        title: this.params.onAffiliationText ?? 'Аффилиация',
        icons: ['fas', 'university'],
        condition: () => {
          return this.params.onAffiliation && this.checkType('onAffiliation')
        },
        action: () => {
          this.params.onAffiliation(this.params)
        }
      },
      {
        title: this.params.onUserAddText ?? 'Добавление привязки пользователя',
        icons: ['fas', 'user-plus'],
        condition: () => {
          return this.params.onUserAdd && this.checkType('onUserAdd')
        },
        action: () => {
          this.params.onUserAdd(this.params)
        }
      },
      {
        title: this.params.onUserRemoveText ?? 'Удаление привязки пользователя',
        icons: ['fas', 'user-minus'],
        condition: () => {
          return this.params.onUserRemove && this.checkType('onUserRemove')
        },
        action: () => {
          this.params.onUserRemove(this.params)
        }
      },
      {
        title: this.params.onInfoText ?? 'Информация',
        icons: ['fas', 'info'],
        condition: () => {
          return this.params.onInfo && this.checkType('onInfo')
        },
        action: () => {
          this.params.onInfo(this.params)
        }
      },
      {
        title: this.params.onLinkText ?? 'Открыть ссылку',
        icons: ['fas', 'external-link-alt'],
        condition: () => {
          return this.params.onLink && this.checkType('onLink')
        },
        action: () => {
          this.params.onLink(this.params)
        }
      },
      {
        title: this.params.onCopyText ?? 'Найти похожие',
        icons: ['fas', 'clone'],
        condition: () => {
          return this.params.onCopy && this.checkType('onCopy')
        },
        action: () => {
          this.params.onCopy(this.params)
        }
      },
      {
        title: this.params.onRequestText ?? 'Создать заявку',
        icons: ['fas', 'file-invoice-dollar'],
        condition: () => {
          return this.params.onRequest && this.checkType('onRequest')
        },
        action: () => {
          this.params.onRequest(this.params)
        }
      },
      {
        title: this.params.onQuartilesText ?? 'Параметры журнала',
        icons: ['fas', 'book'],
        condition: () => {
          return this.params.onQuartiles && this.checkType('onQuartiles')
        },
        action: () => {
          this.params.onQuartiles(this.params)
        }
      },
      {
        title: this.params.onReturnText ?? 'Вернуть на редактирование',
        icons: ['fas', 'undo'],
        condition: () => {
          return this.params.onReturn && this.checkType('onReturn')
        },
        action: () => {
          this.params.onReturn(this.params)
        }
      },
      {
        title: this.params.onDeleteText ?? 'Удалить',
        icons: ['fas', 'times'],
        condition: () => {
          return this.params.onDelete && this.checkType('onDelete')
        },
        action: () => {
          this.params.onDelete(this.params)
        },
        disabled: this.params.deleteEnabled
          ? !this.params.deleteEnabled(this.params)
          : false
      }
    ]
    this.buttons = buttons.filter(b => b.condition())
  }
}
</script>

<style lang="scss" scoped>
.ActionRenderer__Buttons {
  display: flex;
  vertical-align: top;
  padding: 3px;
  width: min-content;
}
</style>
